/**
 * Creates a monochrome preview of a decoded source while preserving its alpha.
 * The original image or canvas remains available for canonical project rendering.
 *
 * @param source Loaded image or masked canvas whose visible pixels receive the color.
 * @param color Resolved single preview color.
 * @returns Detached tinted canvas, or the original source when canvas is unavailable.
 */
export function colorizeImageSource(
	source: HTMLCanvasElement | HTMLImageElement,
	color: string,
): HTMLCanvasElement | HTMLImageElement {
	if (typeof document === 'undefined') {
		return source;
	}

	const canvas = document.createElement('canvas');

	canvas.width = ('naturalWidth' in source && source.naturalWidth) || source.width;
	canvas.height = ('naturalHeight' in source && source.naturalHeight) || source.height;
	const context = canvas.getContext('2d');

	if (!context) {
		return source;
	}

	context.drawImage(
		source,
		0,
		0,
		canvas.width,
		canvas.height,
	);
	context.globalCompositeOperation = 'source-in';
	context.fillStyle = color;
	context.fillRect(
		0,
		0,
		canvas.width,
		canvas.height,
	);

	return canvas;
}

/**
 * Rotates a decoded browser image into a canvas-backed source while preserving its center.
 *
 * Quarter turns exchange the source dimensions; other angles retain the original
 * bounding box to preserve the established renderer behavior. A zero-equivalent
 * rotation returns the original element without allocating a canvas.
 *
 * @param image Decoded image used as the rotation source.
 * @param degrees Clockwise rotation in degrees.
 * @returns Original image for zero-equivalent or unavailable transforms, otherwise the rotated canvas.
 */
export function rotateImageSource(
	image: HTMLImageElement,
	degrees: number,
): HTMLCanvasElement | HTMLImageElement {
	const normalizedDegrees = ((degrees % 360) + 360) % 360;

	if (
		normalizedDegrees === 0
		|| typeof document === 'undefined'
	) {
		return image;
	}

	const swapsDimensions = normalizedDegrees === 90 || normalizedDegrees === 270;
	const canvas = document.createElement('canvas');

	canvas.width = swapsDimensions
		? image.naturalHeight
		: image.naturalWidth;
	canvas.height = swapsDimensions
		? image.naturalWidth
		: image.naturalHeight;

	const context = canvas.getContext('2d');

	if (!context) {
		return image;
	}

	context.translate(
		canvas.width / 2,
		canvas.height / 2,
	);
	context.rotate(normalizedDegrees * Math.PI / 180);
	context.drawImage(
		image,
		-image.naturalWidth / 2,
		-image.naturalHeight / 2,
	);

	return canvas;
}
